import * as THREE from "three";
import {
  ARCHETYPE_COUNT,
  MAX_ARCHETYPE_INSTANCES,
  getTier,
} from "../core/config";
import { SpatialHash } from "../core/SpatialHash";
import { terrainHeightMeters } from "../core/TokyoTerrain";
import {
  chunkCoordinate,
  fillWorldAround,
  type WorldItem,
} from "../core/WorldGenerator";

export interface ConsumedLookup {
  has(id: number): boolean;
  add(id: number): void;
}

const GEOMETRY_FACTORIES: ReadonlyArray<() => THREE.BufferGeometry> = [
  () => new THREE.IcosahedronGeometry(1, 0),
  () => new THREE.BoxGeometry(1.55, 1.15, 1.55, 1, 1, 1),
  () => new THREE.ConeGeometry(0.95, 2.15, 5, 1),
  () => new THREE.CylinderGeometry(0.72, 1.02, 1.9, 6, 1),
  () => new THREE.DodecahedronGeometry(1, 0),
];

const ARCHETYPE_COLORS = [
  0x69e7ff,
  0xff8f78,
  0xc7f36a,
  0xc9a7ff,
  0xffd76f,
] as const;

export class WorldLayer {
  readonly group = new THREE.Group();
  readonly tierIndex: number;

  private readonly meshes: THREE.InstancedMesh[] = [];
  private readonly materials: THREE.MeshStandardMaterial[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly itemsByArchetype: WorldItem[][] = Array.from(
    { length: ARCHETYPE_COUNT },
    () => [],
  );
  private readonly spatialHash: SpatialHash<WorldItem>;
  private readonly dummy = new THREE.Object3D();
  private readonly color = new THREE.Color();
  private readonly baseColor = new THREE.Color();
  private readonly highlightColor = new THREE.Color(0xffffff);
  private readonly candidateBuffer: WorldItem[] = [];
  private readonly tierMetersPerUnit: number;
  private items: WorldItem[] = [];
  private centerChunkX = Number.NaN;
  private centerChunkZ = Number.NaN;
  private originXMeters = 0;
  private originZMeters = 0;
  private density: number;
  private currentOpacity = 1;

  constructor(
    tierIndex: number,
    private readonly consumed: ConsumedLookup,
    density: number,
    playerXMeters: number,
    playerZMeters: number,
  ) {
    this.tierIndex = tierIndex;
    this.tierMetersPerUnit = getTier(tierIndex).metersPerUnit;
    this.density = density;
    this.group.name = `tier-${tierIndex}-world`;
    this.spatialHash = new SpatialHash(getTier(tierIndex).metersPerUnit * 3.2);
    this.createMeshes();
    this.regenerate(playerXMeters, playerZMeters);
  }

  get activeCount(): number {
    return this.itemsByArchetype.reduce((total, entries) => total + entries.length, 0);
  }

  get totalSlots(): number {
    return this.items.length;
  }

  get drawCalls(): number {
    return this.meshes.reduce((total, mesh) => total + (mesh.count > 0 ? 1 : 0), 0);
  }

  set opacity(value: number) {
    this.currentOpacity = Math.max(0, Math.min(1, value));
    for (const material of this.materials) {
      const transparent = this.currentOpacity < 0.999;
      if (material.transparent !== transparent) {
        material.transparent = transparent;
        material.needsUpdate = true;
      }
      material.opacity = this.currentOpacity;
      material.depthWrite = this.currentOpacity >= 0.999;
    }
    this.group.visible = this.currentOpacity > 0.005;
  }

  get opacity(): number {
    return this.currentOpacity;
  }

  updateStreaming(playerXMeters: number, playerZMeters: number): boolean {
    const nextChunkX = chunkCoordinate(playerXMeters, this.tierIndex);
    const nextChunkZ = chunkCoordinate(playerZMeters, this.tierIndex);
    if (nextChunkX === this.centerChunkX && nextChunkZ === this.centerChunkZ) {
      return false;
    }
    this.regenerate(playerXMeters, playerZMeters);
    return true;
  }

  setDensity(density: number, playerXMeters: number, playerZMeters: number): void {
    if (Math.abs(this.density - density) < 0.01) {
      return;
    }
    this.density = density;
    this.regenerate(playerXMeters, playerZMeters);
  }

  updateMatrices(
    playerXMeters: number,
    playerZMeters: number,
    renderMetersPerUnit: number,
    elapsedSeconds: number,
  ): void {
    void elapsedSeconds;
    const scale = this.tierMetersPerUnit / renderMetersPerUnit;
    this.group.scale.setScalar(scale);
    this.group.position.set(
      (this.originXMeters - playerXMeters) / renderMetersPerUnit,
      -terrainHeightMeters(playerXMeters, playerZMeters, this.tierIndex)
        / renderMetersPerUnit,
      (this.originZMeters - playerZMeters) / renderMetersPerUnit,
    );
  }

  queryNearby(xMeters: number, zMeters: number, radiusMeters: number): WorldItem[] {
    const candidates = this.spatialHash.queryCircleInto(
      this.candidateBuffer,
      xMeters,
      zMeters,
      radiusMeters,
    );
    let activeCount = 0;
    for (const item of candidates) {
      if (item.active) {
        candidates[activeCount] = item;
        activeCount += 1;
      }
    }
    candidates.length = activeCount;
    return candidates;
  }

  collect(item: WorldItem): void {
    if (!item.active) {
      return;
    }
    item.active = false;
    this.consumed.add(item.id);
    const entries = this.itemsByArchetype[item.archetype];
    const mesh = this.meshes[item.archetype];
    if (!entries || !mesh || item.renderSlot < 0) {
      return;
    }
    const removedSlot = item.renderSlot;
    const lastItem = entries.pop();
    item.renderSlot = -1;
    if (lastItem && lastItem !== item) {
      entries[removedSlot] = lastItem;
      lastItem.renderSlot = removedSlot;
      this.writeItemMatrix(lastItem, removedSlot, mesh);
      this.setItemColor(lastItem, item.archetype, removedSlot, mesh);
    }
    mesh.count = entries.length;
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) {
      mesh.instanceColor.needsUpdate = true;
    }
  }

  clearCircle(xMeters: number, zMeters: number, radiusMeters: number): number {
    const safeRadius = Math.max(0, radiusMeters);
    let cleared = 0;
    for (const item of this.items) {
      if (!item.active) {
        continue;
      }
      const dx = item.xMeters - xMeters;
      const dz = item.zMeters - zMeters;
      const clearanceRadius = safeRadius + item.radiusMeters * 1.1;
      if (dx * dx + dz * dz <= clearanceRadius * clearanceRadius) {
        this.collect(item);
        cleared += 1;
      }
    }
    return cleared;
  }

  findNearestEligible(
    xMeters: number,
    zMeters: number,
    maximumRadiusMeters: number,
  ): WorldItem | null {
    let nearest: WorldItem | null = null;
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (const item of this.items) {
      if (!item.active || item.radiusMeters > maximumRadiusMeters) {
        continue;
      }
      const dx = item.xMeters - xMeters;
      const dz = item.zMeters - zMeters;
      const distance = dx * dx + dz * dz;
      if (distance < nearestDistance) {
        nearest = item;
        nearestDistance = distance;
      }
    }
    return nearest;
  }

  findNearestOversized(
    xMeters: number,
    zMeters: number,
    minimumRadiusMeters: number,
  ): WorldItem | null {
    let nearest: WorldItem | null = null;
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (const item of this.items) {
      if (!item.active || item.radiusMeters <= minimumRadiusMeters) {
        continue;
      }
      const dx = item.xMeters - xMeters;
      const dz = item.zMeters - zMeters;
      const distance = dx * dx + dz * dz;
      if (distance < nearestDistance) {
        nearest = item;
        nearestDistance = distance;
      }
    }
    return nearest;
  }

  dispose(): void {
    this.group.remove(...this.meshes);
    for (const mesh of this.meshes) {
      mesh.dispose();
    }
    for (const geometry of this.geometries) {
      geometry.dispose();
    }
    for (const material of this.materials) {
      material.dispose();
    }
    this.meshes.length = 0;
    this.materials.length = 0;
    this.geometries.length = 0;
    this.items.length = 0;
    this.spatialHash.clear();
  }

  private createMeshes(): void {
    const tier = getTier(this.tierIndex);
    for (let archetype = 0; archetype < ARCHETYPE_COUNT; archetype += 1) {
      const factory = GEOMETRY_FACTORIES[archetype];
      if (!factory) {
        continue;
      }
      const geometry = factory();
      const archetypeColor = ARCHETYPE_COLORS[archetype] ?? tier.accent;
      const material = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        emissive: archetypeColor,
        emissiveIntensity: 0.11,
        roughness: 0.58 - archetype * 0.025,
        metalness: archetype % 2 === 0 ? 0.025 : 0.07,
        flatShading: true,
        vertexColors: true,
        transparent: false,
        opacity: 1,
      });
      material.name = `tier-${tier.id}-material-${archetype}`;
      const mesh = new THREE.InstancedMesh(geometry, material, MAX_ARCHETYPE_INSTANCES);
      mesh.name = `tier-${tier.id}-archetype-${archetype}`;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.frustumCulled = false;
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      this.geometries.push(geometry);
      this.materials.push(material);
      this.meshes.push(mesh);
      this.group.add(mesh);
    }
  }

  private regenerate(playerXMeters: number, playerZMeters: number): void {
    const generated = fillWorldAround(
      this.items,
      this.tierIndex,
      playerXMeters,
      playerZMeters,
      this.density,
    );
    this.items = generated.items;
    this.centerChunkX = generated.centerChunkX;
    this.centerChunkZ = generated.centerChunkZ;
    this.originXMeters = generated.centerChunkX * generated.chunkSizeMeters;
    this.originZMeters = generated.centerChunkZ * generated.chunkSizeMeters;
    for (const list of this.itemsByArchetype) {
      list.length = 0;
    }
    for (const item of this.items) {
      if (this.consumed.has(item.id)) {
        item.active = false;
      }
      item.renderSlot = -1;
      if (item.active) {
        const entries = this.itemsByArchetype[item.archetype];
        if (entries) {
          item.renderSlot = entries.length;
          entries.push(item);
        }
      }
    }
    this.spatialHash.rebuild(this.items);
    this.assignColors();
    this.writeAllMatrices();
    this.updateMatrices(playerXMeters, playerZMeters, this.tierMetersPerUnit, 0);
  }

  private assignColors(): void {
    const tier = getTier(this.tierIndex);
    this.baseColor.set(tier.accent);
    for (let archetype = 0; archetype < ARCHETYPE_COUNT; archetype += 1) {
      const mesh = this.meshes[archetype];
      const entries = this.itemsByArchetype[archetype];
      if (!mesh || !entries) {
        continue;
      }
      for (let slot = 0; slot < entries.length; slot += 1) {
        const item = entries[slot];
        if (!item) {
          continue;
        }
        this.setItemColor(item, archetype, slot, mesh);
      }
      if (mesh.instanceColor) {
        mesh.instanceColor.needsUpdate = true;
      }
    }
  }

  private writeAllMatrices(): void {
    for (let archetype = 0; archetype < ARCHETYPE_COUNT; archetype += 1) {
      const mesh = this.meshes[archetype];
      const entries = this.itemsByArchetype[archetype];
      if (!mesh || !entries) {
        continue;
      }
      mesh.count = entries.length;
      for (let slot = 0; slot < entries.length; slot += 1) {
        const item = entries[slot];
        if (item) {
          this.writeItemMatrix(item, slot, mesh);
        }
      }
      mesh.instanceMatrix.needsUpdate = true;
    }
  }

  private writeItemMatrix(
    item: WorldItem,
    slot: number,
    mesh: THREE.InstancedMesh,
  ): void {
    const localRadius = item.radiusMeters / this.tierMetersPerUnit;
    this.dummy.position.set(
      (item.xMeters - this.originXMeters) / this.tierMetersPerUnit,
      item.surfaceYMeters / this.tierMetersPerUnit + localRadius * item.heightScale,
      (item.zMeters - this.originZMeters) / this.tierMetersPerUnit,
    );
    this.dummy.rotation.set(0, item.rotation, item.archetype === 2 ? 0.08 : 0);
    this.dummy.scale.set(
      localRadius,
      localRadius * item.heightScale,
      localRadius,
    );
    this.dummy.updateMatrix();
    mesh.setMatrixAt(slot, this.dummy.matrix);
  }

  private setItemColor(
    item: WorldItem,
    archetype: number,
    slot: number,
    mesh: THREE.InstancedMesh,
  ): void {
    this.color.set(ARCHETYPE_COLORS[archetype] ?? this.baseColor);
    this.color.lerp(this.baseColor, 0.22);
    this.color.offsetHSL(
      (item.colorIndex - 3) * 0.012,
      -0.02 + (item.colorIndex % 3) * 0.025,
      -0.015 + (item.colorIndex % 4) * 0.028,
    );
    this.color.lerp(this.highlightColor, 0.1);
    if (item.rare) {
      // Rare objects reuse the same instance pool and draw call; the warm
      // white/gold instance color is the deterministic gameplay affordance.
      this.color.set(0xffd34f).lerp(this.highlightColor, 0.42);
    }
    mesh.setColorAt(slot, this.color);
  }
}
